import React from 'react';
import { useHistory } from 'react-router-dom';
import { Typography, Box, makeStyles } from '@material-ui/core';
import { DetailsLayout } from '../utilities/layouts/DetailsLayout';
import { ContentLayout } from '../utilities/layouts/ContentLayout';

const useStyles = makeStyles(theme => ({
    textItalicUnderline: {
        fontStyle: 'italic',
        textDecoration: 'underline',
    },
    emailRef: { color: 'inherit', textDecoration: 'inherit', fontWeight: 500 },
}));

function PrivacyPolicyPageContent() {
    const classes = useStyles();

    const emailWatchman = (
        <a className={classes.emailRef} href="mailto:watchman@uhn.ca">
            watchman@uhn.ca
        </a>
    );

    return (
        <Box pt={4} pb={24}>
            <Typography variant="h3" paragraph>
                Effective Date: December 1, 2025
            </Typography>
            <Box mb={4}>
                <Typography variant="body1" paragraph>
                    This privacy policy governs your use of the software application Watchman (“Application”) which was created by the Centre for Digital
                    Therapeutics (formally the Centre for Global eHealth Innovation) and the University Health Network (UHN) in collaboration with Dr. Rob
                    Hamilton of the Princess Margaret Cancer Centre. The Centre for Digital Therapeutics is a digital health research and development group that
                    is hosted at UHN.
                </Typography>
                <Typography variant="body1" paragraph>
                    Watchman is an application which intends to support testicular cancer survivors who are in the active surveillance phase of their cancer
                    journey. The Watchman system consists of a patient-facing application (Patient Application) as well as a clinician-facing Dashboard
                    (Clinician Dashboard). This application will improve clinical decision-making by providing patient health information to their respective
                    clinicians. Watchman provides patients access to individual-level testicular-related lab values directly from the
                    Ontario Laboratory Information System (OLIS).
                </Typography>
                <Typography variant="body1" paragraph>
                    Based on the results of the patient-reported outcome survey, lab tests, and imaging reports, clinicians would then generate a care plan, in
                    the form of a doctor's note, for patients to view on the Patient Application. The feedback in the doctor's note may tell patients of their
                    next virtual follow-up visit date, schedule a phone or video call, or inform the patient that an in-person visit is needed. Furthermore, the
                    Patient Application will enable asynchronous multimedia communication between patients and their care team.
                </Typography>
                <Typography variant="body1">
                    Watchman is not a medical device and is not intended to diagnose, cure, mitigate, treat or prevent disease or other conditions.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    Our Commitment to Privacy
                </Typography>
                <Typography variant="body1" gutterBottom>
                    This Privacy Policy gives you information on how Watchman collects, manages, and protects your data, including any Personal Health
                    Information (PHI) that you share within the Application, and further describes which specific data may be shared with others, as well as
                    what data will not be shared, and how you are able to control and manage this sharing.
                </Typography>
                <Typography variant="body1">
                    When you choose to create an account with Watchman, you will need to agree to this Privacy Policy and to the Terms and Conditions (which is
                    presented separately). By agreeing to the Privacy Policy, you are providing consent for your PHI to be stored and used by Watchman as
                    presented below.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    Data Collection & Usage
                </Typography>
                <Typography variant="body1" paragraph>
                    The information you provide and receive in Watchman is considered Personal Health Information (PHI). This includes your registration
                    information - name, email address, birth date, OHIP number, your testicular cancer-related health information -treatment group, virtual
                    follow-up visits, reported symptoms, lab requisition, imaging information, lab results, doctor's notes, and other personally identifiable
                    information. Together, such information is referred to as “PHI” within this Privacy Policy.
                </Typography>
                <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                    Registering for an Account
                </Typography>
                <Typography paragraph>
                    If you agree to use Watchman to complete your follow-up visits, you will be required to provide your full name, email address, birth date,
                    and OHIP number to the Watchman Coordinator. This information will be used by the Watchman Coordinator to create your account in the
                    Watchman system. Subsequently, you will receive a registration email at your provided email address. Without this information, the Watchman
                    Coordinator will not be able to create an account for you, and you will not be receiving a registration email.
                </Typography>
                <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                    Setting up your Account
                </Typography>
                <Typography gutterBottom>
                    Once you receive the registration email, there will be a link where you will be able to set up your account in Watchman. In this link, you
                    will be required to verify your identity by providing your birth date and OHIP number. Once verified, you will be asked to create a password
                    and set security questions for your account in Watchman.
                </Typography>
                <Typography paragraph>
                    Once your Watchman account is set up, you will be able to complete your virtual follow-up visits on this Application.
                </Typography>
                <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                    Completing Tasks
                </Typography>
                <Typography paragraph>
                    At the beginning of each virtual follow-up visit, you will be asked to provide the Application with your PHI in order to report your
                    symptoms, get a blood test, and get an imaging test at your community or hospital.
                </Typography>
                <Typography>You will be asked to report your symptoms:</Typography>
                <ul>
                    <li>
                        <Typography gutterBottom>
                            This allows you to report any physiological symptoms that you may be experiencing as a testicular cancer survivor. The symptoms that
                            you provide in the Application will be sent to your clinician to review and assess your health progress.
                        </Typography>
                    </li>
                </ul>
                <Typography>You will be asked to get a blood test at your community or hospital's lab centre:</Typography>
                <ul>
                    <li>
                        <Typography gutterBottom>
                            To get a blood test, you will be required to bring your personalized lab requisition form to a community or hospital's lab centre.
                            Your clinician will generate your personalized lab requisition form through the Watchman system, which will be available for you to
                            download onto your device.
                        </Typography>
                    </li>
                </ul>
                <Typography>You will be asked to get your imaging test at your desired imaging centre:</Typography>
                <ul>
                    <li>
                        <Typography gutterBottom>
                            To get your imaging test, the Watchman Coordinator will send your imaging requisition to your desired
                            imaging centre. You will be required to bring your Ontario Health Insurance Plan (OHIP) card.
                        </Typography>
                    </li>
                </ul>
                <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                    Receiving Lab Results through OLIS
                </Typography>
                <Typography gutterBottom>
                    Once you have completed your lab test at a lab centre, your testicular-related lab results will be made available for you and your clinician
                    to view in the Watchman system. This is possible through our partnership with Ontario Health, which allows the Watchman system to interface
                    with Ontario Laboratory Information System (OLIS).
                </Typography>
                <Typography paragraph>
                    You can view your most recent and past testicular-related lab results in the Application. Your lab results will also be shared with your
                    clinician during each virtual follow-up visit to help them assess your health progress.
                </Typography>
                <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                    Receiving Doctor's Note
                </Typography>
                <Typography paragraph>
                    Your reported symptoms, imaging and lab results will be shared with your clinician. Based on this information, your clinician will assess
                    your health progress and create a care plan, in the form of a doctor's note, for you to view in your account of this Application. The
                    information provided in the doctor's note may tell you of your next virtual follow-up visit date or request to schedule a phone call, video
                    call, or in-person visit with you. Upon receiving a doctor's note, your most recent virtual follow-up visit will be complete.
                </Typography>
                <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                    Automatically Collected Information
                </Typography>
                <Typography gutterBottom>
                    Watchman collects certain information from you automatically, referred to as "Automatically Collected Data", which is limited to analytics
                    around the Application screens you use. Automatically Collected Data will only be used by UHN's administrative team responsible for
                    maintaining the Application and hosting the data submitted, and also by third-party agents of UHN, acting solely on UHN's behalf, for system
                    administration purposes. Access to data will be limited to that necessary for diagnosing server problems (such as software bugs), monitoring
                    traffic patterns, determining which servers are used most frequently, assessing site usage, and improving the usability of Watchman.
                </Typography>
                <Typography gutterBottom>
                    By creating an account with Watchman, you are agreeing to the automatic collection of the data listed above, solely for use by the
                    Application team.
                </Typography>
                <Typography paragraph>
                    In addition to the use of the Automatically Collected Data described above, the Application team may also use your responses to the
                    patient-reported symptom survey to improve the quality of the Application but will ensure that all PHI (ie. name, email address, date of
                    birth, etc.) is stripped before doing so.
                </Typography>
                <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                    Cookies
                </Typography>
                <Typography paragraph>
                    Watchman may use "cookies" for the convenience of users and to enhance your experience using this Application. A cookie is a small text file
                    that is recorded either temporarily or persistently on your computer by a web page server and is not intended to deliver viruses or other
                    harmful programs to your computer.
                </Typography>
                <Typography>On Watchman, cookies are used primarily for authentication and security purposes:</Typography>
                <ul>
                    <li>
                        <Typography gutterBottom>
                            Cookies are used to avoid signing into your account repeatedly with 1+ authentication within a period of 30 days. By using cookies,
                            Watchman is able to deliver a more efficient service that is more personalized for each user.
                        </Typography>
                    </li>
                </ul>
                <Typography>Web browsers generally accept cookies automatically. When you sign in to Watchman, you can:</Typography>
                <ul>
                    <li>
                        <Typography gutterBottom>Opt for the Application to remember you by using cookies or</Typography>
                    </li>
                    <li>
                        <Typography gutterBottom>Choose to set your web browser to decline cookies or</Typography>
                    </li>
                    <li>
                        <Typography gutterBottom>
                            Modify your browser setting to notify you each time a cookie is created and permit you to accept or decline cookies on an individual
                            basis or
                        </Typography>
                    </li>
                    <li>
                        <Typography paragraph>
                            Modify your browser setting to accept cookies from websites that meet certain disclosure and use specifications.
                        </Typography>
                    </li>
                </ul>
                <Typography variant="body1">
                    Note that some functionality of Watchman may be limited if you direct your browser to reject all cookies.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    How We Share Your Data
                </Typography>
                <Box>
                    <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                        With Your Clinician and Watchman Coordinator
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        Only with your explicit consent when you provide registration information to the Watchman Coordinator and by agreeing to the Terms and
                        Conditions when you are setting up your account, will Watchman share your PHI with your clinician and Watchman Coordinator, who is
                        working on behalf of your clinician.
                    </Typography>
                    <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                        With Ontario Laboratory Information System (OLIS)
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        Watchman uses your OHIP number to retrieve your testicular-related lab results from OLIS. By doing so, you and your clinician can view
                        your lab results in the Watchman system, and your clinician can complete their assessment of your health progress for your virtual
                        follow-up visits.
                    </Typography>
                    <Typography variant="body2" className={classes.textItalicUnderline} gutterBottom>
                        With Third Parties
                    </Typography>
                    <Typography variant="body1" paragraph>
                        We will not sell, rent, or share your PHI with third parties other than as outlined herein.
                    </Typography>
                    <Typography variant="body1">
                        We may disclose your PHI as required by law, such as to comply with a subpoena or similar legal process.
                    </Typography>
                </Box>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    How You Can Control Your Data
                </Typography>
                <Typography variant="body1">
                    You will have control over your PHI. If at any point your identity information needs to be corrected, you can contact the Watchman Support
                    Team by email at {emailWatchman}.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    Participation
                </Typography>
                <Typography variant="body1">
                    Participation in Watchman is voluntary. If you decide at any point that you no longer want to complete virtual follow-up visits in Watchman,
                    and you want to return to in-person follow-up visits, you may do so by calling your doctor's office to pause your Watchman account and make
                    an appointment for you return to an in-person follow-up visit.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    How We Store Your Data
                </Typography>
                <Typography variant="body1" paragraph>
                    Watchman is governed by Ontario and Canadian law. All information gathered from you will be stored and processed in Ontario, Canada on
                    University Health Network servers. If you access your account in the Watchman from a location other than Canada, your information will be
                    transferred to Canada.
                </Typography>
                <Typography variant="body1">
                    Watchman may retain your PHI for as long as you use the Application. You may submit a request with your provider to discontinue your
                    participation in Watchman, subsequently stopping the collection of any new PHI in the Application. Historical PHI in Watchman will be
                    handled in accordance with this Privacy Policy. When a patient is discharged from the program, we will retain the records for at least ten
                    years as per UHN policy.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    How We Keep Your Data Secure
                </Typography>
                <Typography variant="body1">
                    Watchman has implemented security measures intended to protect your PHI both online and offline, including measures intended to restrict
                    unauthorized persons from accessing PHI. Encryption technologies are used when transferring and receiving data provided through Watchman. We
                    also have security measures in place at our physical facilities to protect against the loss, misuse, or alteration of PHI stored at
                    Watchman. We also have restricted access to the servers for the Application, and have the ability to audit who accesses your data and when.
                    Although we have implemented various measures to protect your information, Watchman does not and cannot, guarantee the absolute security of
                    any information.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    Theft, Loss or Unauthorized Access of Your Information
                </Typography>
                <Typography variant="body1">
                    In the event that UHN becomes aware that your PHI submitted through Watchman has been stolen or lost, a person has obtained unauthorized
                    access to it, or someone has used, disclosed or disposed of it in a manner other than as described in this policy, UHN will investigate the
                    incident and notify you if a breach has occurred. If you become aware of any unauthorized use of your PHI submitted through this
                    Application, please immediately inform the UHN Privacy Office at the address below.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    Modifications and Updates to the Privacy Policy
                </Typography>
                <Typography variant="body1">
                    This Privacy Policy may be updated from time to time for any reason. We will notify you of any changes to our Privacy Policy by posting the
                    updated version here and informing you via Watchman's website. By providing your email upon registering with the Application, you are
                    confirming your permission to be notified via email of any changes to, or requests to update or expand, your consent as outlined in this
                    document. Please also check this Privacy Policy regularly for any changes.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    Applicable Law
                </Typography>
                <Typography variant="body1">
                    This Privacy Policy governs your use of the Application. This policy was created by the Centre for Digital Therapeutics, UHN in compliance
                    with the requirements of Ontario's privacy legislation, the Personal Health Information Protection Act (PHIPA). Without regard to conflicts
                    of law principles, the exclusive jurisdiction and venue for any dispute arising out of this Privacy Policy shall be the Provincial courts of
                    Ontario or the Federal courts of Canada.
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    Contact Us
                </Typography>
                <Typography variant="body1" gutterBottom>
                    For information, questions, or complaints, please write to:
                </Typography>
                <Typography variant="body1" gutterBottom>
                    University Health Network
                    <br />
                    Centre for Digital Therapeutics
                    <br />
                    Toronto General Hospital,
                    <br />
                    R. Fraser Elliot Building, 4th Floor
                    <br />
                    200 Elizabeth Street
                    <br />
                    Toronto, ON, M5G 2C4
                </Typography>
                <Typography variant="body1" paragraph>
                    Telephone: 416-340-4800 x4765
                    <br />
                    Email: {emailWatchman}
                </Typography>
                <Typography variant="body1">
                    For more detailed information about who sees your information and about your choices, contact the UHN Privacy Office:
                    <br />
                    Telephone: 416-340-4800 ext. 6937
                    <br />
                    Email:
                    <a className={classes.emailRef} href="mailto:privacy@uhn.on.ca">
                        {' '}
                        privacy@uhn.on.ca
                    </a>
                </Typography>
            </Box>
            <Box mb={4}>
                <Typography variant="h3" paragraph>
                    Complaints
                </Typography>
                <Typography variant="body1" gutterBottom>
                    If you have complaints about UHN's compliance with this privacy policy or PHIPA, please contact UHN's Privacy Office at the above contact
                    information. Further questions and concerns can be directed to the Office of the Information and Privacy Commissioner of Ontario at:
                </Typography>
                <Typography variant="body1" gutterBottom>
                    2 Bloor Street East
                    <br />
                    Suite 1400
                    <br />
                    Toronto, ON
                    <br />
                    M4W 1A8
                </Typography>
                <Typography variant="body1" gutterBottom>
                    Phone: 1-800-387-0073
                    <br />
                    Website:
                    <a className={classes.emailRef} href="https://www.ipc.on.ca/">
                        {' '}
                        www.ipc.on.ca
                    </a>
                </Typography>
                <Box fontStyle="italic">
                    <Typography variant="body2" display="inline">
                        Last Updated:{' '}
                    </Typography>
                    <Typography variant="body1" display="inline">
                        on December 1, 2025
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default function PrivacyPolicyPage() {
    const title = 'Privacy Policy';
    const history = useHistory();

    const onPrevClick = () => {
        history.goBack();
    };

    return (
        <DetailsLayout title={title} onClick={onPrevClick}>
            <ContentLayout>
                <PrivacyPolicyPageContent />
            </ContentLayout>
        </DetailsLayout>
    );
}
